import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Button,
  LinearProgress,
  Chip,
  IconButton,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  Refresh as RetryIcon,
  CloudUpload as UploadingIcon,
  Close as CloseIcon,
  Send as SendIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { PLATFORM_CONFIG, PLATFORM_ICONS } from './PLATFORM_CONFIG';

// Status config for visual rendering
const STATUS_META = {
  queued: {
    label: 'Uploading…',
    color: 'text.secondary',
    chipColor: 'default',
    icon: <CircularProgress size={14} color="inherit" />,
    showProgress: true,
    showRetry: false,
  },
  uploading: {
    label: 'Posting...',
    color: 'primary.main',
    chipColor: 'primary',
    icon: <UploadingIcon fontSize="small" />,
    showProgress: true,
    showRetry: false,
  },
  staging_cloud: {
    label: 'Staging to Cloud...',
    color: 'secondary.main',
    chipColor: 'secondary',
    icon: <UploadingIcon fontSize="small" />,
    showProgress: true,
    showRetry: false,
  },
  success: {
    label: 'Posted',
    color: 'success.main',
    chipColor: 'success',
    icon: <CheckCircleIcon fontSize="small" />,
    showProgress: false,
    showRetry: false,
  },
  error: {
    label: 'Failed',
    color: 'error.main',
    chipColor: 'error',
    icon: <ErrorIcon fontSize="small" />,
    showProgress: false,
    showRetry: true,
  },
};

function PlatformStatusRow({ platform, status, result, onRetry, retrying }) {
  const config = PLATFORM_CONFIG[platform];
  const icon = PLATFORM_ICONS[platform];
  const statusMeta = STATUS_META[status] || STATUS_META.queued;

  return (
    <Box
      sx={{
        p: 2,
        mb: 1,
        borderRadius: 2,
        border: `1px solid ${status === 'success'
            ? 'rgba(34, 197, 94, 0.3)'
            : status === 'error'
              ? 'rgba(239, 68, 68, 0.3)'
              : status === 'uploading' || status === 'staging_cloud'
                ? alpha(config.color, 0.3)
                : 'rgba(0, 0, 0, 0.08)'
          }`,
        background:
          status === 'success'
            ? 'rgba(34, 197, 94, 0.04)'
            : status === 'error'
              ? 'rgba(239, 68, 68, 0.04)'
              : status === 'uploading' || status === 'staging_cloud'
                ? alpha(config.color, 0.04)
                : 'transparent',
        transition: 'all 0.3s ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* Platform avatar */}
        <Avatar
          sx={{
            bgcolor: alpha(config.color, 0.12),
            color: config.color,
            width: 36,
            height: 36,
            flexShrink: 0,
          }}
        >
          {icon}
        </Avatar>

        {/* Label + sub-text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {config.label}
          </Typography>
          {status === 'error' && result?.error && (
            <Typography
              variant="caption"
              sx={{
                color: 'error.main',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={result.error}
            >
              {result.error}
            </Typography>
          )}
          {status === 'success' && result?.post_id && (
            <Typography variant="caption" sx={{ color: 'success.main', display: 'block' }}>
              Post ID: {result.post_id}
            </Typography>
          )}
        </Box>

        {/* Status chip */}
        <Chip
          icon={statusMeta.icon}
          label={statusMeta.label}
          color={statusMeta.chipColor}
          size="small"
          variant={status === 'queued' ? 'outlined' : 'filled'}
          sx={{ fontWeight: 600, flexShrink: 0 }}
        />

        {/* Retry button */}
        {statusMeta.showRetry && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={retrying ? null : <RetryIcon />}
            disabled={retrying}
            onClick={() => onRetry(platform, result?.content)}
            sx={{ borderRadius: 2, fontWeight: 600, minWidth: 72, flexShrink: 0 }}
          >
            {retrying ? '...' : 'Retry'}
          </Button>
        )}
      </Box>

      {/* Uploading progress bar */}
      {statusMeta.showProgress && (
        <LinearProgress
          sx={{ mt: 1.5, borderRadius: 1, height: 3 }}
          color="primary"
        />
      )}
    </Box>
  );
}

/**
 * Smart posting progress modal.
 *
 * Props:
 *  open             - bool
 *  platforms        - string[] of platform keys being posted to
 *  postingStatus    - { [platform]: 'queued' | 'uploading' | 'success' | 'error' }
 *  results          - { [platform]: { post_id, error, content } }
 *  retryingPlatform - string | null (which platform is currently retrying)
 *  onRetry          - (platform, content) => void
 *  onClose          - () => void
 *  allDone          - bool (all platforms have a final status)
 */
const PostingProgressModal = React.memo(({
  open,
  platforms = [],
  postingStatus = {},
  results = {},
  retryingPlatform,
  onRetry,
  onClose,
  allDone,
}) => {
  const successCount = platforms.filter((p) => postingStatus[p] === 'success').length;
  const errorCount = platforms.filter((p) => postingStatus[p] === 'error').length;
  const queuedCount = platforms.filter((p) => postingStatus[p] === 'queued').length;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={allDone ? onClose : undefined}  // only closeable when done
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      {/* Title bar */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          color: 'white',
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 36, height: 36 }}>
          <SendIcon fontSize="small" />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Posting to {platforms.length} Platform{platforms.length > 1 ? 's' : ''}
          </Typography>
          {allDone && (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {successCount > 0 && `${successCount} succeeded`}
              {successCount > 0 && errorCount > 0 && ' · '}
              {errorCount > 0 && `${errorCount} failed`}
            </Typography>
          )}
        </Box>
        {allDone && (
          <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {/* Platform rows */}
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {!allDone && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Please wait while your content is being posted…
          </Typography>
        )}

        {platforms.map((platform) => (
          <PlatformStatusRow
            key={platform}
            platform={platform}
            status={postingStatus[platform] || 'queued'}
            result={results[platform]}
            onRetry={onRetry}
            retrying={retryingPlatform === platform}
          />
        ))}
      </DialogContent>

      {/* Footer actions */}
      <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        {allDone && queuedCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
            <InfoIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.1, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              YouTube is uploading in the background — you can close this and continue using the app. The status will update automatically when done.
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {allDone && errorCount === 0 && queuedCount === 0 && (
            <Typography variant="body2" sx={{ flex: 1, color: 'success.main', fontWeight: 600, alignSelf: 'center' }}>
              All posts published successfully!
            </Typography>
          )}
          {allDone && (
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              }}
            >
              Done
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
});

export default PostingProgressModal;
